import type { LearningDomain, Subject } from '@/content/schema';
import { bank, lesson, m, MOE_REF, objective, opts, SAMPLE, type ObjectiveInput } from '../helpers';

export const subject: Subject = {
  id: 'arabic',
  name: { ar: 'العربيّة', fr: 'Arabe', en: 'Arabic' },
  glyph: 'ض',
  color: 'arabic',
  teachingLanguage: 'ar',
  levels: ['prep', 'y1', 'y2', 'y3', 'y4', 'y5', 'y6'],
  sources: MOE_REF,
  ...SAMPLE,
};

export const domains: LearningDomain[] = [
  {
    id: 'ar.letters',
    subjectId: 'arabic',
    name: { ar: 'الحروف والأصوات', fr: 'Lettres et sons', en: 'Letters and sounds' },
  },
  {
    id: 'ar.vocabulary',
    subjectId: 'arabic',
    name: { ar: 'الرصيد اللغوي', fr: 'Vocabulaire', en: 'Vocabulary' },
  },
  {
    id: 'ar.writing',
    subjectId: 'arabic',
    name: { ar: 'الكتابة والرسم', fr: 'Écriture et orthographe', en: 'Writing and spelling' },
  },
  {
    id: 'ar.grammar',
    subjectId: 'arabic',
    name: { ar: 'قواعد اللغة', fr: 'Grammaire', en: 'Grammar' },
  },
  {
    id: 'ar.reading',
    subjectId: 'arabic',
    name: { ar: 'القراءة والفهم', fr: 'Lecture et compréhension', en: 'Reading comprehension' },
  },
];

const o = (input: ObjectiveInput) => objective('arabic', 'ar', input);
const shape = 'similar-letter-shape' as const;
const sound = 'similar-sound' as const;

export const objectives = [
  o({
    id: 'ar.letters',
    domainId: 'ar.letters',
    levels: ['prep', 'y1'],
    order: 10,
    difficulty: 1,
    title: { ar: 'أتعرّف على الحروف', fr: 'Reconnaître les lettres', en: 'Recognising letters' },
    description: {
      ar: 'يميّز الحروف المتشابهة في الشكل ويربطها بأسمائها.',
      fr: 'Distinguer des lettres de forme proche et les associer à leur nom.',
      en: 'Tell apart letters with similar shapes and match them to their names.',
    },
    games: ['quick-quiz', 'find-correct', 'letter-sound', 'memory', 'true-false'],
  }),
  o({
    id: 'ar.letter-sounds',
    domainId: 'ar.letters',
    levels: ['y1'],
    order: 20,
    difficulty: 1,
    prerequisites: ['ar.letters'],
    title: {
      ar: 'الحرف الأوّل من الكلمة',
      fr: 'La première lettre du mot',
      en: 'The first letter of a word',
    },
    description: {
      ar: 'يسمع كلمة ويتعرّف على الحرف الذي تبدأ به.',
      fr: 'Entendre un mot et reconnaître la lettre par laquelle il commence.',
      en: 'Hear a word and recognise the letter it starts with.',
    },
    games: ['letter-sound', 'quick-quiz', 'find-correct', 'memory'],
  }),
  o({
    id: 'ar.harakat',
    domainId: 'ar.letters',
    levels: ['y1'],
    order: 30,
    difficulty: 2,
    prerequisites: ['ar.letters'],
    title: { ar: 'الحركات القصيرة', fr: 'Les voyelles courtes', en: 'Short vowel marks' },
    description: {
      ar: 'يميّز الفتحة والضمّة والكسرة على الحرف.',
      fr: 'Distinguer fatha, damma et kasra sur une lettre.',
      en: 'Tell apart fatha, damma and kasra on a letter.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'ar.vocabulary',
    domainId: 'ar.vocabulary',
    levels: ['y1', 'y2'],
    order: 40,
    difficulty: 1,
    title: { ar: 'كلمات من محيطي', fr: 'Les mots de mon entourage', en: 'Everyday words' },
    description: {
      ar: 'يسمّي أشياء وحيوانات من محيطه اليومي.',
      fr: 'Nommer des objets et des animaux du quotidien.',
      en: 'Name everyday objects and animals.',
    },
    games: ['quick-quiz', 'find-correct', 'memory', 'true-false'],
  }),
  o({
    id: 'ar.letter-positions',
    domainId: 'ar.letters',
    levels: ['y2'],
    order: 50,
    difficulty: 2,
    prerequisites: ['ar.letter-sounds'],
    title: {
      ar: 'موقع الحرف في الكلمة',
      fr: 'La place de la lettre dans le mot',
      en: 'Letter position in a word',
    },
    description: {
      ar: 'يحدّد موقع الحرف في أوّل الكلمة أو وسطها أو آخرها ويتعرّف على شكله.',
      fr: 'Repérer une lettre au début, au milieu ou à la fin du mot et reconnaître sa forme.',
      en: 'Find a letter at the start, middle or end of a word and recognise its shape.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'ar.word-building',
    domainId: 'ar.writing',
    levels: ['y2'],
    order: 60,
    difficulty: 2,
    prerequisites: ['ar.letter-sounds'],
    title: {
      ar: 'أركّب كلمة من حروفها',
      fr: 'Former un mot avec ses lettres',
      en: 'Building words from letters',
    },
    description: {
      ar: 'يرتّب حروفًا متفرّقة ليكوّن كلمة صحيحة.',
      fr: 'Remettre des lettres dans l’ordre pour former un mot.',
      en: 'Put letters in order to make a word.',
    },
    games: ['word-builder', 'quick-quiz'],
  }),
  o({
    id: 'ar.opposites',
    domainId: 'ar.vocabulary',
    levels: ['y3'],
    order: 70,
    difficulty: 2,
    prerequisites: ['ar.vocabulary'],
    title: { ar: 'الأضداد', fr: 'Les contraires', en: 'Opposites' },
    description: {
      ar: 'يجد ضدّ الكلمة ويربط الكلمات بأضدادها.',
      fr: 'Trouver le contraire d’un mot.',
      en: 'Find the opposite of a word.',
    },
    games: ['memory', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'ar.sentence-building',
    domainId: 'ar.grammar',
    levels: ['y3'],
    order: 80,
    difficulty: 3,
    prerequisites: ['ar.word-building'],
    title: { ar: 'أركّب جملة مفيدة', fr: 'Construire une phrase', en: 'Building a sentence' },
    description: {
      ar: 'يرتّب كلمات ليكوّن جملة مفيدة.',
      fr: 'Ordonner des mots pour former une phrase.',
      en: 'Put words in order to make a sentence.',
    },
    games: ['sentence-builder', 'quick-quiz'],
  }),
  o({
    id: 'ar.spelling',
    domainId: 'ar.writing',
    levels: ['y3'],
    order: 90,
    difficulty: 3,
    prerequisites: ['ar.word-building'],
    title: {
      ar: 'التاء المربوطة والتاء المفتوحة',
      fr: 'Le tā’ lié et le tā’ ouvert',
      en: 'Closed and open tā’',
    },
    description: {
      ar: 'يختار الكتابة الصحيحة لكلمات تنتهي بالتاء أو بالهاء.',
      fr: 'Choisir l’orthographe correcte des mots finissant par tā’ ou hā’.',
      en: 'Choose the correct spelling of words ending in tā’ or hā’.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'ar.sentence-types',
    domainId: 'ar.grammar',
    levels: ['y4'],
    order: 100,
    difficulty: 3,
    prerequisites: ['ar.sentence-building'],
    title: {
      ar: 'الجملة الاسميّة والجملة الفعليّة',
      fr: 'Phrase nominale et phrase verbale',
      en: 'Nominal and verbal sentences',
    },
    description: {
      ar: 'يميّز الجملة التي تبدأ باسم من الجملة التي تبدأ بفعل.',
      fr: 'Distinguer la phrase qui commence par un nom de celle qui commence par un verbe.',
      en: 'Tell a sentence starting with a noun from one starting with a verb.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'ar.number',
    domainId: 'ar.grammar',
    levels: ['y4'],
    order: 110,
    difficulty: 3,
    prerequisites: ['ar.vocabulary'],
    title: {
      ar: 'المفرد والمثنّى والجمع',
      fr: 'Singulier, duel et pluriel',
      en: 'Singular, dual and plural',
    },
    description: {
      ar: 'يميّز المفرد والمثنّى والجمع ويكوّن المثنّى.',
      fr: 'Distinguer singulier, duel et pluriel.',
      en: 'Tell singular, dual and plural apart.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'ar.reading',
    domainId: 'ar.reading',
    levels: ['y4', 'y5', 'y6'],
    order: 120,
    difficulty: 3,
    prerequisites: ['ar.sentence-building'],
    title: { ar: 'أقرأ وأفهم', fr: 'Lire et comprendre', en: 'Read and understand' },
    description: {
      ar: 'يقرأ نصًّا قصيرًا ويجيب عن أسئلة حوله.',
      fr: 'Lire un court texte et répondre à des questions.',
      en: 'Read a short text and answer questions about it.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'ar.grammar-roles',
    domainId: 'ar.grammar',
    levels: ['y5', 'y6'],
    order: 130,
    difficulty: 4,
    prerequisites: ['ar.sentence-types'],
    title: {
      ar: 'الفاعل والمفعول به',
      fr: 'Le sujet et le complément d’objet',
      en: 'Subject and object',
    },
    description: {
      ar: 'يحدّد الفاعل والمفعول به في جملة فعليّة ويتعرّف على علامة إعراب كلّ منهما.',
      fr: 'Identifier le sujet et le complément d’objet dans une phrase verbale.',
      en: 'Identify the subject and the object in a verbal sentence.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
];

const READING_1 =
  'خَرَجَتْ سَلْمَى مَعَ جَدِّهَا إِلَى السُّوقِ صَبَاحَ يَوْمِ الْأَحَدِ. اشْتَرَى الْجَدُّ خُبْزًا وَزَيْتُونًا، وَاشْتَرَتْ سَلْمَى كِتَابًا جَدِيدًا عَنِ الْبَحْرِ. فِي طَرِيقِ الْعَوْدَةِ جَلَسَا تَحْتَ شَجَرَةِ زَيْتُونٍ كَبِيرَةٍ، وَقَرَأَتْ سَلْمَى لِجَدِّهَا أَوَّلَ صَفْحَةٍ مِنْ كِتَابِهَا.';
const READING_2 =
  'يَعِيشُ النَّحْلُ فِي خَلَايَا. تَطِيرُ النَّحْلَةُ مِنْ زَهْرَةٍ إِلَى زَهْرَةٍ لِتَجْمَعَ الرَّحِيقَ، ثُمَّ تَعُودُ إِلَى الْخَلِيَّةِ لِتَصْنَعَ الْعَسَلَ. وَيُسَاعِدُ النَّحْلُ الْأَزْهَارَ عَلَى أَنْ تُصْبِحَ ثِمَارًا.';

export const items = [
  ...bank('ar.letters', 1, [
    {
      kind: 'choice',
      prompt: 'أين حرف الباء؟',
      audio: { text: 'باء' },
      ...opts('ب', m('ت', shape), m('ث', shape), m('ن', shape)),
      hint: 'حرف الباء تحته نقطة واحدة.',
    },
    {
      kind: 'choice',
      prompt: 'أين حرف الجيم؟',
      audio: { text: 'جيم' },
      ...opts('ج', m('ح', shape), m('خ', shape)),
      hint: 'حرف الجيم نقطته في داخله.',
    },
    {
      kind: 'choice',
      prompt: 'أين حرف السين؟',
      audio: { text: 'سين' },
      ...opts('س', m('ش', shape), m('ص', shape)),
      hint: 'حرف السين بلا نقاط، وله ثلاث أسنان صغيرة.',
    },
    {
      kind: 'choice',
      prompt: 'أين حرف الدال؟',
      audio: { text: 'دال' },
      ...opts('د', m('ذ', shape), m('ر', shape)),
      hint: 'حرف الدال بلا نقطة، ولا ينزل تحت السطر.',
    },
    {
      kind: 'choice',
      prompt: 'أين حرف العين؟',
      audio: { text: 'عين' },
      ...opts('ع', m('غ', shape), m('ف', shape)),
      hint: 'حرف العين بلا نقطة.',
    },
    {
      kind: 'choice',
      prompt: 'أين حرف الخاء؟',
      audio: { text: 'خاء' },
      ...opts('خ', m('ح', shape), m('ج', shape)),
      hint: 'حرف الخاء نقطته فوقه.',
    },
    { kind: 'trueFalse', statement: 'هذا حرف الراء: ر', answer: true },
    {
      kind: 'trueFalse',
      statement: 'هذا حرف الزاي: ر',
      answer: false,
      mistake: shape,
      explanation: 'هذا حرف الراء. حرف الزاي فوقه نقطة: ز.',
    },
    {
      kind: 'match',
      prompt: 'اربط كلّ حرف باسمه.',
      pairs: [
        { left: 'ب', right: 'باء' },
        { left: 'ج', right: 'جيم' },
        { left: 'د', right: 'دال' },
        { left: 'س', right: 'سين' },
        { left: 'ع', right: 'عين' },
        { left: 'م', right: 'ميم' },
      ],
      mistake: shape,
    },
  ]),
  ...bank('ar.letter-sounds', 1, [
    {
      kind: 'choice',
      prompt: 'بأيّ حرف تبدأ كلمة «سَمَكَة»؟',
      visual: { emoji: '🐟', label: 'سَمَكَة' },
      audio: { text: 'سَمَكَة' },
      ...opts('س', m('ص', sound), m('ش', shape)),
    },
    {
      kind: 'choice',
      prompt: 'بأيّ حرف تبدأ كلمة «قَمَر»؟',
      visual: { emoji: '🌙', label: 'قَمَر' },
      audio: { text: 'قَمَر' },
      ...opts('ق', m('ك', sound), m('ف', shape)),
    },
    {
      kind: 'choice',
      prompt: 'بأيّ حرف تبدأ كلمة «جَمَل»؟',
      visual: { emoji: '🐪', label: 'جَمَل' },
      audio: { text: 'جَمَل' },
      ...opts('ج', m('ح', shape), m('خ', shape)),
    },
    {
      kind: 'choice',
      prompt: 'بأيّ حرف تبدأ كلمة «مَوْز»؟',
      visual: { emoji: '🍌', label: 'مَوْز' },
      audio: { text: 'مَوْز' },
      ...opts('م', m('ن', sound), m('ل', 'unknown')),
    },
    {
      kind: 'choice',
      prompt: 'بأيّ حرف تبدأ كلمة «بَطَّة»؟',
      visual: { emoji: '🦆', label: 'بَطَّة' },
      audio: { text: 'بَطَّة' },
      ...opts('ب', m('ت', shape), m('ن', shape)),
    },
    {
      kind: 'choice',
      prompt: 'بأيّ حرف تبدأ كلمة «وَرْدَة»؟',
      visual: { emoji: '🌹', label: 'وَرْدَة' },
      audio: { text: 'وَرْدَة' },
      ...opts('و', m('ر', shape), m('ز', shape)),
    },
    {
      kind: 'choice',
      prompt: 'بأيّ حرف تبدأ كلمة «عِنَب»؟',
      visual: { emoji: '🍇', label: 'عِنَب' },
      audio: { text: 'عِنَب' },
      ...opts('ع', m('غ', shape), m('أ', sound)),
    },
    {
      kind: 'choice',
      prompt: 'بأيّ حرف تبدأ كلمة «تُفَّاحَة»؟',
      visual: { emoji: '🍎', label: 'تُفَّاحَة' },
      audio: { text: 'تُفَّاحَة' },
      ...opts('ت', m('ط', sound), m('ث', shape)),
    },
    {
      kind: 'match',
      prompt: 'اربط كلّ حرف بكلمة تبدأ به.',
      pairs: [
        { left: 'ب', right: 'بَطَّة', emoji: '🦆' },
        { left: 'س', right: 'سَمَكَة', emoji: '🐟' },
        { left: 'ج', right: 'جَمَل', emoji: '🐪' },
        { left: 'م', right: 'مَوْز', emoji: '🍌' },
        { left: 'ع', right: 'عِنَب', emoji: '🍇' },
        { left: 'و', right: 'وَرْدَة', emoji: '🌹' },
      ],
      mistake: sound,
    },
  ]),
  ...bank('ar.harakat', 2, [
    {
      kind: 'choice',
      prompt: 'أيّ حرف عليه فتحة؟',
      ...opts('بَ', m('بُ', 'harakat'), m('بِ', 'harakat')),
      hint: 'الفتحة خطّ صغير فوق الحرف.',
    },
    {
      kind: 'choice',
      prompt: 'أيّ حرف عليه ضمّة؟',
      ...opts('تُ', m('تَ', 'harakat'), m('تِ', 'harakat')),
      hint: 'الضمّة تشبه واوًا صغيرة فوق الحرف.',
    },
    {
      kind: 'choice',
      prompt: 'أيّ حرف تحته كسرة؟',
      ...opts('سِ', m('سَ', 'harakat'), m('سُ', 'harakat')),
      hint: 'الكسرة خطّ صغير تحت الحرف.',
    },
    {
      kind: 'choice',
      prompt: 'ما حركة الحرف «دِ»؟',
      ...opts('الكسرة', m('الفتحة', 'harakat'), m('الضمّة', 'harakat')),
    },
    {
      kind: 'choice',
      prompt: 'ما حركة الحرف «رُ»؟',
      ...opts('الضمّة', m('الفتحة', 'harakat'), m('الكسرة', 'harakat')),
    },
    {
      kind: 'choice',
      prompt: 'ما حركة الحرف «لَ»؟',
      ...opts('الفتحة', m('الكسرة', 'harakat'), m('الضمّة', 'harakat')),
    },
    {
      kind: 'choice',
      prompt: 'اختر الكلمة التي أوّلها مضموم:',
      ...opts('أُمّ', m('أَب', 'harakat'), m('إِبْرَة', 'harakat')),
    },
    { kind: 'trueFalse', statement: 'على حرف الكاف في «كَ» فتحة.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'على حرف النون في «نِ» ضمّة.',
      answer: false,
      mistake: 'harakat',
      explanation: 'تحت النون في «نِ» كسرة.',
    },
  ]),
  ...bank('ar.vocabulary', 1, [
    {
      kind: 'choice',
      prompt: 'ما اسم هذا؟',
      visual: { emoji: '🏠', label: 'بيت' },
      ...opts('بَيْت', m('شَجَرَة', 'vocabulary'), m('قَلَم', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'ما اسم هذه؟',
      visual: { emoji: '🌳', label: 'شجرة' },
      ...opts('شَجَرَة', m('زَهْرَة', 'vocabulary'), m('بَيْت', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'ما اسم هذه؟',
      visual: { emoji: '☀️', label: 'شمس' },
      ...opts('شَمْس', m('قَمَر', 'vocabulary'), m('نَجْمَة', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'ما اسم هذا الحيوان؟',
      visual: { emoji: '🐄', label: 'بقرة' },
      ...opts('بَقَرَة', m('حِصَان', 'vocabulary'), m('خَرُوف', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'ما اسم هذا؟',
      visual: { emoji: '🍞', label: 'خبز' },
      ...opts('خُبْز', m('حَلِيب', 'vocabulary'), m('تَمْر', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'ما اسم هذه؟',
      visual: { emoji: '🎒', label: 'محفظة' },
      ...opts('مِحْفَظَة', m('كِتَاب', 'vocabulary'), m('كُرَّاس', 'vocabulary')),
    },
    {
      kind: 'trueFalse',
      statement: 'هذه تُفَّاحَة.',
      visual: { emoji: '🍎', label: 'تفاحة' },
      answer: true,
    },
    {
      kind: 'trueFalse',
      statement: 'هذه بُرْتُقَالَة.',
      visual: { emoji: '🍌', label: 'موزة' },
      answer: false,
      mistake: 'vocabulary',
      explanation: 'هذه مَوْزَة.',
    },
    {
      kind: 'match',
      prompt: 'اربط كلّ صورة باسمها.',
      pairs: [
        { left: '🐱', right: 'قِطّ' },
        { left: '🐶', right: 'كَلْب' },
        { left: '🐟', right: 'سَمَكَة' },
        { left: '🐦', right: 'عُصْفُور' },
        { left: '🐴', right: 'حِصَان' },
        { left: '🐑', right: 'خَرُوف' },
      ],
      mistake: 'vocabulary',
    },
  ]),
  ...bank('ar.letter-positions', 2, [
    {
      kind: 'choice',
      prompt: 'في أيّ كلمة جاء حرف العين في وسطها؟',
      ...opts('نَعْجَة', m('عَسَل', 'letter-position'), m('رَبِيع', 'letter-position')),
    },
    {
      kind: 'choice',
      prompt: 'في أيّ كلمة جاء حرف الميم في آخرها؟',
      ...opts('قَلَم', m('مَوْز', 'letter-position'), m('سَمَكَة', 'letter-position')),
    },
    {
      kind: 'choice',
      prompt: 'في أيّ كلمة جاء حرف الباء في وسطها؟',
      ...opts('سَبُّورَة', m('بَيْت', 'letter-position'), m('كِتَاب', 'letter-position')),
    },
    {
      kind: 'choice',
      prompt: 'كيف يُكتب حرف الهاء في وسط الكلمة؟',
      ...opts('ـهـ', m('هـ', 'letter-position'), m('ـه', 'letter-position')),
    },
    {
      kind: 'choice',
      prompt: 'كيف يُكتب حرف العين في أوّل الكلمة؟',
      ...opts('عـ', m('ـعـ', 'letter-position'), m('ـع', 'letter-position')),
    },
    {
      kind: 'choice',
      prompt: 'كيف يُكتب حرف الكاف في آخر الكلمة بعد حرف يتّصل به؟',
      ...opts('ـك', m('كـ', 'letter-position'), m('ـكـ', 'letter-position')),
    },
    {
      kind: 'trueFalse',
      statement: 'في كلمة «مَدْرَسَة» جاء حرف الدال في وسط الكلمة.',
      answer: true,
    },
    {
      kind: 'trueFalse',
      statement: 'في كلمة «فِيل» جاء حرف الفاء في آخر الكلمة.',
      answer: false,
      mistake: 'letter-position',
      explanation: 'الفاء في أوّل كلمة «فِيل».',
    },
  ]),
  ...bank('ar.word-building', 2, [
    {
      kind: 'order',
      prompt: 'رتّب الحروف لتكوّن كلمة:',
      unit: 'letter',
      visual: { emoji: '✏️', label: 'قلم' },
      tokens: ['ق', 'ل', 'م'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الحروف لتكوّن كلمة:',
      unit: 'letter',
      visual: { emoji: '📘', label: 'كتاب' },
      tokens: ['ك', 'ت', 'ا', 'ب'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الحروف لتكوّن كلمة:',
      unit: 'letter',
      visual: { emoji: '🚪', label: 'باب' },
      tokens: ['ب', 'ا', 'ب'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الحروف لتكوّن كلمة:',
      unit: 'letter',
      visual: { emoji: '🌙', label: 'قمر' },
      tokens: ['ق', 'م', 'ر'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الحروف لتكوّن كلمة:',
      unit: 'letter',
      visual: { emoji: '🐟', label: 'سمكة' },
      tokens: ['س', 'م', 'ك', 'ة'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الحروف لتكوّن كلمة:',
      unit: 'letter',
      visual: { emoji: '🌳', label: 'شجرة' },
      tokens: ['ش', 'ج', 'ر', 'ة'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الحروف لتكوّن كلمة:',
      unit: 'letter',
      visual: { emoji: '🍌', label: 'موز' },
      tokens: ['م', 'و', 'ز'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الحروف لتكوّن كلمة:',
      unit: 'letter',
      visual: { emoji: '🏠', label: 'بيت' },
      tokens: ['ب', 'ي', 'ت'],
      mistake: 'letter-order',
    },
  ]),
  ...bank('ar.opposites', 2, [
    {
      kind: 'choice',
      prompt: 'ما ضدّ كلمة «فَرِح»؟',
      ...opts('حَزِين', m('سَعِيد', 'vocabulary'), m('ضَاحِك', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'ما ضدّ كلمة «قَرِيب»؟',
      ...opts('بَعِيد', m('جَمِيل', 'vocabulary'), m('صَغِير', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'ما ضدّ كلمة «مَفْتُوح»؟',
      ...opts('مُغْلَق', m('وَاسِع', 'vocabulary'), m('جَدِيد', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'ما ضدّ كلمة «صَبَاح»؟',
      ...opts('مَسَاء', m('نَهَار', 'vocabulary'), m('شَمْس', 'vocabulary')),
    },
    { kind: 'trueFalse', statement: 'ضدّ «نَظِيف» هو «وَسِخ».', answer: true },
    {
      kind: 'trueFalse',
      statement: 'ضدّ «ثَقِيل» هو «كَبِير».',
      answer: false,
      mistake: 'vocabulary',
      explanation: 'ضدّ «ثَقِيل» هو «خَفِيف».',
    },
    {
      kind: 'match',
      prompt: 'اربط كلّ كلمة بضدّها.',
      pairs: [
        { left: 'كَبِير', right: 'صَغِير' },
        { left: 'طَوِيل', right: 'قَصِير' },
        { left: 'سَاخِن', right: 'بَارِد' },
        { left: 'نَهَار', right: 'لَيْل' },
        { left: 'فَوْق', right: 'تَحْت' },
        { left: 'سَرِيع', right: 'بَطِيء' },
      ],
      mistake: 'vocabulary',
    },
  ]),
  ...bank('ar.sentence-building', 3, [
    {
      kind: 'order',
      prompt: 'رتّب الكلمات لتكوّن جملة مفيدة:',
      unit: 'word',
      tokens: ['ذَهَبَ', 'أَحْمَدُ', 'إِلَى', 'الْمَدْرَسَةِ'],
      alternatives: [['أَحْمَدُ', 'ذَهَبَ', 'إِلَى', 'الْمَدْرَسَةِ']],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الكلمات لتكوّن جملة مفيدة:',
      unit: 'word',
      tokens: ['تَقْرَأُ', 'مَرْيَمُ', 'قِصَّةً'],
      alternatives: [['مَرْيَمُ', 'تَقْرَأُ', 'قِصَّةً']],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الكلمات لتكوّن جملة مفيدة:',
      unit: 'word',
      tokens: ['الْقِطُّ', 'يَلْعَبُ', 'فِي', 'الْحَدِيقَةِ'],
      alternatives: [['يَلْعَبُ', 'الْقِطُّ', 'فِي', 'الْحَدِيقَةِ']],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الكلمات لتكوّن جملة مفيدة:',
      unit: 'word',
      tokens: ['يَشْرَبُ', 'الطِّفْلُ', 'الْحَلِيبَ'],
      alternatives: [['الطِّفْلُ', 'يَشْرَبُ', 'الْحَلِيبَ']],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الكلمات لتكوّن جملة مفيدة:',
      unit: 'word',
      tokens: ['زَرَعَ', 'الْفَلَّاحُ', 'شَجَرَةً'],
      alternatives: [['الْفَلَّاحُ', 'زَرَعَ', 'شَجَرَةً']],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'رتّب الكلمات لتكوّن جملة مفيدة:',
      unit: 'word',
      tokens: ['السَّمَاءُ', 'صَافِيَةٌ', 'الْيَوْمَ'],
      alternatives: [['الْيَوْمَ', 'السَّمَاءُ', 'صَافِيَةٌ']],
      mistake: 'word-order',
    },
  ]),
  ...bank('ar.spelling', 3, [
    {
      kind: 'choice',
      prompt: 'اختر الكتابة الصحيحة:',
      visual: { emoji: '🏫', label: 'مدرسة' },
      ...opts('مَدْرَسَة', m('مَدْرَسَت', 'spelling'), m('مَدْرَسَه', 'spelling')),
    },
    {
      kind: 'choice',
      prompt: 'اختر الكتابة الصحيحة:',
      visual: { emoji: '👧', label: 'بنت' },
      ...opts('بِنْت', m('بِنَة', 'spelling'), m('بِنْه', 'spelling')),
    },
    {
      kind: 'choice',
      prompt: 'اختر الكتابة الصحيحة:',
      visual: { emoji: '🌳', label: 'شجرة' },
      ...opts('شَجَرَة', m('شَجَرَت', 'spelling'), m('شَجَرَه', 'spelling')),
    },
    {
      kind: 'choice',
      prompt: 'اختر الكتابة الصحيحة:',
      ...opts('زَيْت', m('زَيْة', 'spelling'), m('زَيْه', 'spelling')),
    },
    {
      kind: 'choice',
      prompt: 'اختر الكلمة المكتوبة كتابة صحيحة:',
      ...opts('هَذَا', m('هَاذَا', 'spelling'), m('هَادَا', 'spelling')),
    },
    {
      kind: 'choice',
      prompt: 'اختر الكلمة المكتوبة كتابة صحيحة:',
      ...opts('لَكِنَّ', m('لَاكِنَّ', 'spelling'), m('لَكِنْنَ', 'spelling')),
    },
    {
      kind: 'trueFalse',
      statement: 'تُكتب كلمة «مُعَلِّمَة» بتاء مربوطة.',
      answer: true,
      explanation: 'نقف عليها بالهاء فنكتبها تاءً مربوطة.',
    },
    {
      kind: 'trueFalse',
      statement: 'تُكتب كلمة «سَكَتَ» بتاء مربوطة.',
      answer: false,
      mistake: 'spelling',
      explanation: 'التاء في آخر الفعل تُكتب مفتوحة: سَكَتَ.',
    },
  ]),
  ...bank('ar.sentence-types', 3, [
    {
      kind: 'categorize',
      prompt: 'صنّف الجمل:',
      categories: [
        { id: 'nominal', label: 'جملة اسميّة' },
        { id: 'verbal', label: 'جملة فعليّة' },
      ],
      entries: [
        { text: 'الْوَلَدُ مُجْتَهِدٌ', category: 'nominal' },
        { text: 'كَتَبَ التِّلْمِيذُ الدَّرْسَ', category: 'verbal' },
        { text: 'الْجَوُّ جَمِيلٌ', category: 'nominal' },
        { text: 'يَلْعَبُ الْأَطْفَالُ', category: 'verbal' },
        { text: 'الْبَحْرُ هَادِئٌ', category: 'nominal' },
        { text: 'نَامَتِ الْقِطَّةُ', category: 'verbal' },
      ],
      mistake: 'grammar-role',
      hint: 'انظر إلى الكلمة الأولى: هل هي اسم أم فعل؟',
    },
    {
      kind: 'categorize',
      prompt: 'صنّف الجمل:',
      categories: [
        { id: 'nominal', label: 'جملة اسميّة' },
        { id: 'verbal', label: 'جملة فعليّة' },
      ],
      entries: [
        { text: 'طَلَعَتِ الشَّمْسُ', category: 'verbal' },
        { text: 'الْحَدِيقَةُ وَاسِعَةٌ', category: 'nominal' },
        { text: 'سَافَرَ أَبِي', category: 'verbal' },
        { text: 'الْمَاءُ بَارِدٌ', category: 'nominal' },
        { text: 'تُغَرِّدُ الْعَصَافِيرُ', category: 'verbal' },
      ],
      mistake: 'grammar-role',
    },
    {
      kind: 'choice',
      prompt: 'ما نوع جملة «طَلَعَتِ الشَّمْسُ»؟',
      ...opts('جملة فعليّة', m('جملة اسميّة', 'grammar-role')),
    },
    {
      kind: 'choice',
      prompt: 'ما نوع جملة «الْحَدِيقَةُ وَاسِعَةٌ»؟',
      ...opts('جملة اسميّة', m('جملة فعليّة', 'grammar-role')),
    },
    { kind: 'trueFalse', statement: 'الجملة التي تبدأ بفعل جملة فعليّة.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'جملة «الْعُصْفُورُ يُغَرِّدُ» جملة فعليّة.',
      answer: false,
      mistake: 'grammar-role',
      explanation: 'تبدأ الجملة باسم «الْعُصْفُورُ»، فهي جملة اسميّة.',
    },
  ]),
  ...bank('ar.number', 3, [
    {
      kind: 'categorize',
      prompt: 'صنّف الكلمات:',
      categories: [
        { id: 'singular', label: 'مفرد' },
        { id: 'dual', label: 'مثنّى' },
        { id: 'plural', label: 'جمع' },
      ],
      entries: [
        { text: 'كِتَاب', category: 'singular' },
        { text: 'كِتَابَانِ', category: 'dual' },
        { text: 'كُتُب', category: 'plural' },
        { text: 'تِلْمِيذَتَانِ', category: 'dual' },
        { text: 'أَقْلَام', category: 'plural' },
        { text: 'شَجَرَة', category: 'singular' },
        { text: 'مُعَلِّمُونَ', category: 'plural' },
      ],
      mistake: 'agreement',
    },
    {
      kind: 'choice',
      prompt: 'ما مثنّى «قَلَم»؟',
      ...opts('قَلَمَانِ', m('أَقْلَام', 'agreement'), m('قَلَمٌ', 'agreement')),
    },
    {
      kind: 'choice',
      prompt: 'ما جمع «وَلَد»؟',
      ...opts('أَوْلَاد', m('وَلَدَانِ', 'agreement'), m('وِلَادَة', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'ما مفرد «أَشْجَار»؟',
      ...opts('شَجَرَة', m('شَجَرَتَانِ', 'agreement'), m('أَشْجَارٌ', 'agreement')),
    },
    { kind: 'trueFalse', statement: 'كلمة «بِنْتَانِ» مثنّى.', answer: true },
    {
      kind: 'trueFalse',
      statement: 'كلمة «أَشْجَار» مثنّى.',
      answer: false,
      mistake: 'agreement',
      explanation: '«أَشْجَار» جمع، ومثنّى «شَجَرَة» هو «شَجَرَتَانِ».',
    },
  ]),
  ...bank('ar.reading', 3, [
    {
      kind: 'choice',
      passage: READING_1,
      prompt: 'مع مَن خرجت سلمى؟',
      ...opts('مع جدّها', m('مع أمّها', 'comprehension'), m('مع أخيها', 'comprehension')),
    },
    {
      kind: 'choice',
      passage: READING_1,
      prompt: 'متى خرجت سلمى إلى السوق؟',
      ...opts(
        'صباح يوم الأحد',
        m('مساء يوم السبت', 'comprehension'),
        m('ظهر يوم الجمعة', 'comprehension'),
      ),
    },
    {
      kind: 'choice',
      passage: READING_1,
      prompt: 'ماذا اشترت سلمى؟',
      ...opts(
        'كتابًا عن البحر',
        m('خبزًا وزيتونًا', 'comprehension'),
        m('قصّة عن الغابة', 'comprehension'),
      ),
    },
    {
      kind: 'trueFalse',
      passage: READING_1,
      statement: 'جلست سلمى وجدّها تحت شجرة زيتون.',
      answer: true,
    },
    {
      kind: 'choice',
      passage: READING_2,
      prompt: 'أين يعيش النحل؟',
      ...opts('في الخلايا', m('في الأعشاش', 'comprehension'), m('في الجحور', 'comprehension')),
    },
    {
      kind: 'choice',
      passage: READING_2,
      prompt: 'ماذا تجمع النحلة من الأزهار؟',
      ...opts('الرحيق', m('الماء', 'comprehension'), m('الأوراق', 'comprehension')),
    },
    {
      kind: 'trueFalse',
      passage: READING_2,
      statement: 'يصنع النحل العسل في الحقول.',
      answer: false,
      mistake: 'comprehension',
      explanation: 'جاء في النصّ أنّ النحلة تعود إلى الخليّة لتصنع العسل.',
    },
  ]),
  ...bank('ar.grammar-roles', 4, [
    {
      kind: 'choice',
      prompt: 'ما الفاعل في جملة «كَتَبَ التِّلْمِيذُ الدَّرْسَ»؟',
      ...opts('التِّلْمِيذُ', m('الدَّرْسَ', 'grammar-role'), m('كَتَبَ', 'grammar-role')),
      hint: 'الفاعل هو مَن قام بالفعل.',
    },
    {
      kind: 'choice',
      prompt: 'ما المفعول به في جملة «قَطَفَتْ سَلْمَى وَرْدَةً»؟',
      ...opts('وَرْدَةً', m('سَلْمَى', 'grammar-role'), m('قَطَفَتْ', 'grammar-role')),
      hint: 'المفعول به هو ما وقع عليه الفعل.',
    },
    {
      kind: 'choice',
      prompt: 'ما الفاعل في جملة «يَسْقِي الْفَلَّاحُ الزَّرْعَ»؟',
      ...opts('الْفَلَّاحُ', m('الزَّرْعَ', 'grammar-role'), m('يَسْقِي', 'grammar-role')),
    },
    {
      kind: 'choice',
      prompt: 'ما المفعول به في جملة «فَتَحَ الْحَارِسُ الْبَابَ»؟',
      ...opts('الْبَابَ', m('الْحَارِسُ', 'grammar-role'), m('فَتَحَ', 'grammar-role')),
    },
    {
      kind: 'choice',
      prompt: 'ما علامة رفع الفاعل في «نَجَحَ التِّلْمِيذُ»؟',
      ...opts('الضمّة', m('الفتحة', 'grammar-role'), m('الكسرة', 'grammar-role')),
    },
    {
      kind: 'choice',
      prompt: 'ما علامة نصب المفعول به في «شَرِبَ الطِّفْلُ الْمَاءَ»؟',
      ...opts('الفتحة', m('الضمّة', 'grammar-role'), m('الكسرة', 'grammar-role')),
    },
    {
      kind: 'trueFalse',
      statement: 'في جملة «أَكَلَ الْقِطُّ السَّمَكَةَ» الفاعل هو «السَّمَكَةَ».',
      answer: false,
      mistake: 'grammar-role',
      explanation: 'القطّ هو الذي أكل، فالفاعل «الْقِطُّ»، و«السَّمَكَةَ» مفعول به.',
    },
  ]),
];

export const lessons = [
  lesson('ar.letters', 'الحروف المتشابهة', [
    { type: 'intro', text: 'بعض الحروف تتشابه في شكلها، ونفرّق بينها بالنقاط.' },
    {
      type: 'illustration',
      figure: {
        type: 'cards',
        cards: [
          { emoji: 'ب', title: 'نقطة تحت' },
          { emoji: 'ت', title: 'نقطتان فوق' },
          { emoji: 'ث', title: 'ثلاث نقاط فوق' },
        ],
      },
    },
    {
      type: 'example',
      text: 'ب تحتها نقطة، ت فوقها نقطتان، ث فوقها ثلاث نقاط.',
      expression: 'ب  ت  ث',
    },
    { type: 'tip', text: 'انظر جيّدًا: أين النقاط؟ وكم عددها؟' },
  ]),
  lesson('ar.letter-sounds', 'أسمع الحرف الأوّل', [
    { type: 'intro', text: 'كلّ كلمة تبدأ بصوت. نقول الكلمة ببطء لنسمع حرفها الأوّل.' },
    {
      type: 'example',
      text: 'سَـ… سَمَكَة: تبدأ بحرف السين.',
      visual: { emoji: '🐟', label: 'سمكة' },
    },
    { type: 'tip', text: 'بعض الأصوات متقاربة مثل السين والصاد، فاستمع جيّدًا.' },
  ]),
  lesson('ar.harakat', 'الفتحة والضمّة والكسرة', [
    { type: 'intro', text: 'الحركات علامات صغيرة تغيّر صوت الحرف.' },
    {
      type: 'illustration',
      figure: {
        type: 'cards',
        cards: [
          { emoji: 'بَ', title: 'الفتحة', text: 'فوق الحرف' },
          { emoji: 'بُ', title: 'الضمّة', text: 'فوق الحرف' },
          { emoji: 'بِ', title: 'الكسرة', text: 'تحت الحرف' },
        ],
      },
    },
    { type: 'example', text: 'بَ بالفتحة، بُ بالضمّة، بِ بالكسرة.', expression: 'بَ  بُ  بِ' },
    {
      type: 'rule',
      title: 'مكان الحركة',
      text: 'الفتحة والضمّة فوق الحرف، والكسرة تحت الحرف. الحركة تغيّر صوت الحرف.',
      examples: [
        { expression: 'سَ  سُ  سِ', text: 'السين بالفتحة، ثمّ بالضمّة، ثمّ بالكسرة.' },
        { expression: 'مَ  مُ  مِ', text: 'الميم بالفتحة، ثمّ بالضمّة، ثمّ بالكسرة.' },
        { expression: 'كَتَبَ', text: 'ثلاثة حروف، كلّها بالفتحة.' },
      ],
    },
  ]),
  lesson('ar.vocabulary', 'أسمّي الأشياء', [
    { type: 'intro', text: 'نتعلّم أسماء الأشياء التي نراها كلّ يوم.' },
    {
      type: 'example',
      text: 'هذه شَمْس، وهذا بَيْت، وهذه شَجَرَة.',
      visual: { emoji: '☀️ 🏠 🌳', label: 'شمس، بيت، شجرة' },
    },
    { type: 'tip', text: 'قل اسم الصورة بصوت عالٍ قبل أن تختار.' },
  ]),
  lesson('ar.letter-positions', 'الحرف في أوّل الكلمة ووسطها وآخرها', [
    { type: 'intro', text: 'يتغيّر شكل بعض الحروف حسب مكانها في الكلمة.' },
    {
      type: 'illustration',
      figure: {
        type: 'cards',
        cards: [
          { emoji: 'هـ', title: 'أوّل الكلمة' },
          { emoji: 'ـهـ', title: 'وسط الكلمة' },
          { emoji: 'ـه', title: 'آخر الكلمة' },
        ],
      },
    },
    {
      type: 'example',
      text: 'الهاء: هـ في أوّل الكلمة، ـهـ في وسطها، ـه في آخرها.',
      expression: 'هـ  ـهـ  ـه',
    },
    { type: 'tip', text: 'اقرأ الكلمة حرفًا حرفًا من اليمين إلى اليسار.' },
  ]),
  lesson('ar.word-building', 'أبني كلمة', [
    { type: 'intro', text: 'نرتّب الحروف من اليمين إلى اليسار لنكتب كلمة.' },
    { type: 'example', text: 'ق + ل + م = قلم', visual: { emoji: '✏️', label: 'قلم' } },
    { type: 'tip', text: 'قل الكلمة ببطء واسمع ترتيب أصواتها.' },
  ]),
  lesson('ar.opposites', 'الكلمة وضدّها', [
    { type: 'intro', text: 'الضدّ كلمة معناها عكس معنى كلمة أخرى.' },
    { type: 'example', text: 'ضدّ «كَبِير» هو «صَغِير»، وضدّ «نَهَار» هو «لَيْل».' },
    { type: 'tip', text: 'اسأل نفسك: ما الكلمة التي تعني العكس تمامًا؟' },
  ]),
  lesson('ar.sentence-building', 'أركّب جملة', [
    { type: 'intro', text: 'الجملة المفيدة كلمات مرتّبة تعطي معنى تامًّا.' },
    {
      type: 'example',
      text: 'ذَهَبَ أَحْمَدُ إِلَى الْمَدْرَسَةِ. ويمكن أن نقول أيضًا: أَحْمَدُ ذَهَبَ إِلَى الْمَدْرَسَةِ.',
    },
    { type: 'tip', text: 'ابحث أوّلًا عن الفعل أو عن مَن قام بالفعل.' },
  ]),
  lesson('ar.spelling', 'التاء في آخر الكلمة', [
    {
      type: 'intro',
      text: 'التاء المربوطة (ة) نقف عليها بالهاء، والتاء المفتوحة (ت) نقف عليها بالتاء.',
    },
    {
      type: 'example',
      text: 'مَدْرَسَة ← نقف عليها: مَدْرَسَهْ، فهي تاء مربوطة. بِنْت ← نقف عليها: بِنْتْ، فهي تاء مفتوحة.',
    },
    {
      type: 'rule',
      title: 'التاء المربوطة والتاء المفتوحة',
      text: 'نقف على آخر الكلمة: إذا نطقنا هاءً فهي تاء مربوطة (ة)، وإذا نطقنا تاءً فهي تاء مفتوحة (ت). التاء في آخر الفعل مفتوحة دائمًا.',
      figure: {
        type: 'cards',
        cards: [
          { emoji: 'ة', title: 'تاء مربوطة', text: 'مَدْرَسَة ← مَدْرَسَهْ' },
          { emoji: 'ت', title: 'تاء مفتوحة', text: 'بِنْت ← بِنْتْ' },
        ],
      },
      examples: [
        { expression: 'شَجَرَة', text: 'نقف: شَجَرَهْ ← تاء مربوطة.' },
        { expression: 'بَيْت', text: 'نقف: بَيْتْ ← تاء مفتوحة.' },
        { expression: 'كَتَبَتْ', text: 'فعل: التاء في آخره مفتوحة.' },
        { expression: 'فَاطِمَة', text: 'نقف: فَاطِمَهْ ← تاء مربوطة.' },
      ],
    },
  ]),
  lesson('ar.sentence-types', 'نوع الجملة', [
    { type: 'intro', text: 'إذا بدأت الجملة باسم فهي اسميّة، وإذا بدأت بفعل فهي فعليّة.' },
    { type: 'example', text: '«الْجَوُّ جَمِيلٌ» جملة اسميّة. «طَلَعَتِ الشَّمْسُ» جملة فعليّة.' },
    {
      type: 'rule',
      title: 'الجملة الاسميّة والجملة الفعليّة',
      text: 'ننظر إلى الكلمة الأولى: إذا كانت اسمًا فالجملة اسميّة، وإذا كانت فعلًا فالجملة فعليّة.',
      examples: [
        { expression: 'الشَّمْسُ مُشْرِقَةٌ', text: 'تبدأ باسم ← جملة اسميّة.' },
        { expression: 'يَلْعَبُ الطِّفْلُ', text: 'تبدأ بفعل ← جملة فعليّة.' },
        { expression: 'أَكَلَ الْقِطُّ', text: 'تبدأ بفعل ← جملة فعليّة.' },
        { expression: 'الْبَيْتُ كَبِيرٌ', text: 'تبدأ باسم ← جملة اسميّة.' },
      ],
    },
  ]),
  lesson('ar.number', 'واحد، اثنان، أكثر', [
    { type: 'intro', text: 'المفرد يدلّ على واحد، والمثنّى على اثنين، والجمع على أكثر من اثنين.' },
    {
      type: 'illustration',
      figure: {
        type: 'cards',
        cards: [
          { emoji: '📘', title: 'كِتَاب', text: 'مفرد' },
          { emoji: '📘📘', title: 'كِتَابَانِ', text: 'مثنّى' },
          { emoji: '📘📘📘', title: 'كُتُب', text: 'جمع' },
        ],
      },
    },
    { type: 'example', text: 'كِتَاب ← كِتَابَانِ ← كُتُب' },
    {
      type: 'rule',
      title: 'المفرد والمثنّى والجمع',
      text: 'نكوّن المثنّى بإضافة «انِ» أو «يْنِ» إلى المفرد. الجمع يدلّ على أكثر من اثنين.',
      examples: [
        { expression: 'قَلَم ← قَلَمَانِ ← أَقْلَام' },
        { expression: 'وَلَد ← وَلَدَانِ ← أَوْلَاد' },
        {
          expression: 'شَجَرَة ← شَجَرَتَانِ ← أَشْجَار',
          text: 'التاء المربوطة تصبح تاءً مفتوحة في المثنّى.',
        },
      ],
    },
  ]),
  lesson('ar.reading', 'أقرأ بتمعّن', [
    { type: 'intro', text: 'نقرأ النصّ مرّة أولى لنفهم موضوعه، ثمّ نقرأ السؤال ونعود إلى النصّ.' },
    { type: 'tip', text: 'الجواب موجود في النصّ: ابحث عن الكلمات المهمّة في السؤال.' },
  ]),
  lesson('ar.grammar-roles', 'مَن فعل؟ وما الذي وقع عليه الفعل؟', [
    { type: 'intro', text: 'الفاعل هو مَن قام بالفعل، والمفعول به هو ما وقع عليه الفعل.' },
    {
      type: 'example',
      text: 'في «كَتَبَ التِّلْمِيذُ الدَّرْسَ»: التِّلْمِيذُ فاعل مرفوع بالضمّة، والدَّرْسَ مفعول به منصوب بالفتحة.',
    },
    {
      type: 'rule',
      title: 'الفاعل والمفعول به',
      text: 'الفاعل مرفوع (غالبًا بالضمّة)، والمفعول به منصوب (غالبًا بالفتحة).',
      examples: [
        {
          expression: 'شَرِبَ الطِّفْلُ الْحَلِيبَ',
          text: 'الطِّفْلُ فاعل، والْحَلِيبَ مفعول به.',
        },
        {
          expression: 'قَرَأَتْ مَرْيَمُ الْقِصَّةَ',
          text: 'مَرْيَمُ فاعل، والْقِصَّةَ مفعول به.',
        },
      ],
    },
    { type: 'tip', text: 'اسأل: مَن كتب؟ ← الفاعل. ماذا كتب؟ ← المفعول به.' },
  ]),
];
